import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@features/auth/store';
import { normalizeAxiosError, NormalizedApiError } from './errors';
import { queryClient } from './queryClient';
import i18n from '@/i18n';
import { ErrorCode } from '@shared/constants/errorCodes';
import { mapBackendAuthResult } from './authMapping';
import type { BackendAuthResultDto } from '@features/auth/types';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5001';

/**
 * Absolute API origin (no trailing slash). Exposed for links that must point at
 * the backend-served document root rather than an axios call — e.g. the public
 * `/sitemap.xml` and `/robots.txt` preview links in the SEO settings screen.
 */
export const apiBaseUrl = baseURL.replace(/\/+$/, '');

/**
 * Backend envelope returned by ALL `/api/v1/*` endpoints.
 *
 *   { data: T | null, items: T[] | null, isSuccessful: boolean,
 *     errors: ErrorItem[] | null, pagination: PaginationInfo | null,
 *     message: string | null }
 *
 * Single-record endpoints populate `data`; list endpoints populate `items` + `pagination`.
 */
export interface ActionResponse<T> {
  data: T | null;
  items: T[] | null;
  isSuccessful: boolean;
  errors: ErrorItem[] | null;
  pagination: PaginationInfo | null;
  message: string | null;
}

export interface ErrorItem {
  code?: string | null;
  message?: string | null;
  field?: string | null;
  // Structured payload the BE attaches to the primary error item (BE `ErrorItem.Data`).
  // Carries machine-readable follow-up data kept off the human-readable `message` —
  // e.g. the 2FA `AUTH_2FA_REQUIRED` challenge token (`errors[0].data.challengeToken`).
  data?: Record<string, unknown> | null;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export const http = axios.create({
  baseURL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
  // #5 — send/receive the httpOnly refresh-token cookie. The BE sets it on
  // login/refresh; pairing it with the BE's CORS AllowCredentials lets the
  // refresh flow work without keeping the refresh token in JS-readable storage.
  withCredentials: true,
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, activeProjectId } = useAuthStore.getState();
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  config.headers.set('Accept-Language', 'tr-TR');
  // REQ-45 / K-C — çoklu-site: aktif proje id'sini tek merkezi noktadan enjekte
  // et. Yoksa BE default project'e düşer (public geriye-uyum). Üye olunmayan
  // projeye gönderilirse BE 403 PROJECT_ACCESS_DENIED döner (aşağıda ele alınır).
  if (activeProjectId) {
    config.headers.set('X-Project-Id', activeProjectId);
  }
  return config;
});

interface RefreshableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

/**
 * K-8 (#24) — cookie tabanlı oturum yenileme.
 *
 * Refresh token JS tarafında hiç tutulmaz; BE'nin login/refresh'te set ettiği
 * httpOnly `emlak_refresh_token` cookie'si `withCredentials` ile taşınır ve
 * `/auth/refresh` boş gövdeyle çağrılır.
 *
 * BE refresh ucu login ile AYNI `AuthResultDto`'yu döner (access token + user +
 * `availableProjects` + `defaultProjectId`). Bu yüzden yanıtın TAMAMINI eşleyip
 * store `setSession`'a veririz: böylece HER refresh proje listesini de tazeler
 * (REQ-45 — proje eklenince/atanınca switcher logout'suz güncellenir). Dönüş
 * yine access token'dır (interceptor 401-retry akışı bozulmasın); başarısızlıkta
 * oturum temizlenir.
 */
export async function refreshSessionFromCookie(): Promise<string | null> {
  const { setSession, clearSession } = useAuthStore.getState();
  try {
    const res = await axios.post<ActionResponse<BackendAuthResultDto>>(
      `${baseURL}/api/v1/auth/refresh`,
      {},
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
    );
    const payload = res.data?.data;
    if (!res.data?.isSuccessful || !payload?.accessToken || !payload.user) {
      clearSession();
      return null;
    }
    // Tam auth sonucu → token + user + availableProjects + defaultProjectId
    // hepsi tek noktadan güncellenir (setSession seçili projeyi normalize eder).
    setSession(mapBackendAuthResult(payload));
    return payload.accessToken;
  } catch (error) {
    // 401 (cookie yok/geçersiz) dahil her hata oturum sonu demektir; hatayı
    // yutup null döneriz — interceptor login'e yönlendirir.
    void error;
    clearSession();
    return null;
  }
}

/**
 * REQ-45 / K-C — üye olunmayan projeye `X-Project-Id` gönderildiğinde BE
 * 403 `PROJECT_ACCESS_DENIED` döner. Kullanıcıyı güvenli (varsayılan) projeye
 * geri al, önceki projenin cache'ini SIFIRLA (stale değil, sil → eski proje verisi
 * görünmez) ve tek bir kullanıcı-dostu uyarı göster (eşzamanlı isteklerde
 * tekrarlamamak için sabit toast id'si).
 */
function handleProjectAccessDenied(): void {
  const { activeProjectId, defaultProjectId, resetActiveProject } = useAuthStore.getState();
  if (activeProjectId && activeProjectId !== defaultProjectId) {
    resetActiveProject();
    void queryClient.resetQueries();
  }
  toast.error(i18n.t('errors:PROJECT_ACCESS_DENIED'), { id: 'project-access-denied' });
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RefreshableConfig | undefined;
    if (
      original &&
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/')
    ) {
      original._retry = true;
      refreshPromise ??= refreshSessionFromCookie();
      const token = await refreshPromise;
      refreshPromise = null;
      if (token) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return http(original);
      }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    const normalized = normalizeAxiosError(error);
    if (
      normalized.status === 403 &&
      normalized.code === ErrorCode.PROJECT_ACCESS_DENIED
    ) {
      handleProjectAccessDenied();
    }
    return Promise.reject(normalized);
  },
);

/**
 * Unwrap a single-record `ActionResponse<T>` envelope. Throws a
 * `NormalizedApiError` if the server reports failure or `data` is missing.
 */
export function unwrap<T>(res: AxiosResponse<ActionResponse<T>>): T {
  const body = res.data;
  if (!body || body.isSuccessful !== true || body.data == null) {
    throw new NormalizedApiError({
      status: res.status,
      code: body?.errors?.[0]?.code ?? `HTTP_${res.status}`,
      message:
        body?.message ?? body?.errors?.[0]?.message ?? 'Sunucu beklenmedik bir yanıt döndürdü.',
      details: body ? { errors: body.errors } : undefined,
    });
  }
  return body.data;
}

/**
 * Unwrap the GUID of a freshly-created resource.
 *
 * The BE is inconsistent across create endpoints: taxonomy creates return the
 * bare id string in `data`, while content creates (articles, …) return the
 * full created entity object. Both are accepted here and the id string is
 * always returned, so callers can safely navigate to `/x/:id` or re-fetch.
 * Throws a `NormalizedApiError` (like `unwrap`) on failure or an unusable shape.
 */
export function unwrapCreatedId(
  res: AxiosResponse<ActionResponse<string | { id: string }>>,
): string {
  const data = unwrap(res);
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && typeof data.id === 'string') return data.id;
  throw new NormalizedApiError({
    status: res.status,
    code: 'INVALID_CREATE_RESPONSE',
    message: 'Sunucu, oluşturma isteğine beklenmeyen biçimde yanıt verdi.',
  });
}

/**
 * Unwrap a list-style `ActionResponse<T>` envelope (uses `items` + `pagination`).
 * Returns `{ items, pagination }`.
 */
export function unwrapList<T>(res: AxiosResponse<ActionResponse<T>>): {
  items: T[];
  pagination: PaginationInfo | null;
} {
  const body = res.data;
  if (!body || body.isSuccessful !== true) {
    throw new NormalizedApiError({
      status: res.status,
      code: body?.errors?.[0]?.code ?? `HTTP_${res.status}`,
      message:
        body?.message ?? body?.errors?.[0]?.message ?? 'Sunucu beklenmedik bir yanıt döndürdü.',
      details: body ? { errors: body.errors } : undefined,
    });
  }
  return { items: body.items ?? [], pagination: body.pagination };
}
